import { useFocusTrap } from '@mantine/hooks';
import { FC, ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { CloseModalButton } from './CloseModalButton';

type BaseModalProps = {
  onClose: VoidFunction;
  isDismissable?: boolean;
  children: ReactNode;
  classNames?: {
    dialog?: string;
    content?: string;
  };
};

export const BaseModal: FC<BaseModalProps> = ({ onClose, isDismissable = true, classNames, children }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const focusTrapRef = useFocusTrap();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (!dialog.open) {
      dialog.showModal();
    }
  }, []);

  return createPortal(
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className={twMerge(
        'rounded-2xl shadow-lg opacity-100 starting:opacity-0 max-w-96 focus-visible:outline-none',
        'motion-safe:transition-opacity motion-safe:duration-300',
        'backdrop:backdrop-blur-[0.5rem] backdrop:bg-gray-950/60 starting:backdrop:opacity-0 backdrop:opacity-100',
        'motion-safe:transition-[display,opacity] motion-safe:backdrop:transition-discrete motion-safe:backdrop:duration-300 motion-reduce:backdrop:transition-none',
        classNames?.dialog
      )}
      // eslint-disable-next-line react/no-unknown-property
      closedby={isDismissable ? 'any' : 'none'}
    >
      <div
        ref={focusTrapRef}
        className={twMerge('z-[99] p-6 flex min-h-60 flex-col gap-4 justify-between bg-primary-background min-w-96 relative', classNames?.content)}
      >
        {isDismissable ? <CloseModalButton onClick={onClose} /> : null}
        {children}
      </div>
    </dialog>,
    document.body
  );
};
